import React from "react";
import { Link } from "react-router-dom";
import { Section } from "../../components/Common";

/**
 * LegalPageLayout
 * ---------------------------------------------------------------
 * Shared shell for the four public legal pages:
 *   /privacy, /terms, /community-guidelines, /disclaimer
 *
 * Design intent:
 *   - No redesign. Reuses the existing <Section> and card-surface
 *     tokens used elsewhere on the site.
 *   - Fully public (no auth guard, no gate).
 *   - Legal copy is authored in English and passed in as `children`.
 *     Interface labels reachable from other pages (footer links, auth
 *     ack line) remain fully i18n-translatable.
 *   - Includes cross-links between the four legal pages at the bottom.
 */

const LEGAL_LINKS = [
  { to: "/privacy", label: "Privacy Policy", testid: "legal-nav-privacy" },
  { to: "/terms", label: "Terms of Use", testid: "legal-nav-terms" },
  { to: "/community-guidelines", label: "Community Guidelines", testid: "legal-nav-community" },
  { to: "/disclaimer", label: "Health & Wellness Disclaimer", testid: "legal-nav-disclaimer" },
];

export default function LegalPageLayout({
  eyebrow,
  title,
  subtitle,
  lastUpdated,
  testid,
  children,
}) {
  return (
    <div data-testid={testid}>
      <Section eyebrow={eyebrow} title={title} subtitle={subtitle}>
        {lastUpdated && (
          <div
            className="text-xs uppercase tracking-[0.24em] gold-text mb-6"
            data-testid="legal-last-updated"
          >
            Last updated: {lastUpdated}
          </div>
        )}

        <article
          className="card-surface p-6 md:p-10 legal-prose text-white/80 leading-relaxed"
          data-testid="legal-body"
        >
          {children}
        </article>

        <div className="mt-10">
          <div className="text-xs uppercase tracking-[0.24em] gold-text mb-3">
            More legal documents
          </div>
          <ul
            className="flex flex-wrap gap-x-6 gap-y-2 text-sm"
            data-testid="legal-crosslinks"
          >
            {LEGAL_LINKS.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  className="text-white/75 hover:text-white underline underline-offset-4 decoration-white/25 hover:decoration-white/60"
                  data-testid={l.testid}
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </Section>
    </div>
  );
}
